import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Calendar,
  MapPin,
  Users,
  Plus,
  Check,
  X,
  ChevronDown,
  Palette,
  Square,
  Circle,
  Heart,
  Star,
  Link as LinkIcon,
  Globe,
  Shirt,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import SearchableSelect from '../../components/UI/SearchableSelect';
import CategoryDropdown from '../../components/UI/CategoryDropdown';
import DatePicker from '../../components/UI/DatePicker';
import TimePicker from '../../components/UI/TimePicker';
import Toast from '../../components/UI/Toast';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAppSelector } from '../../store/hooks';
import { EventFormData, EventCategory, EventInSeriesFormData, Event } from '../../types/event';
import { generateEventViewUrl } from '../../utils/urlUtils';
import { getImageShapeStyles } from '../../utils/imageShapeStyles';
import categoryAPI from '../../services/categoryAPI';
import usersAPI, { HostUser } from '../../services/usersAPI';
import eventAPI from '../../services/eventAPI';
import seriesAPI from '../../services/seriesAPI';
import { useAlert } from '../../contexts';
import { logError } from '../../utils/errorLogger';
import { BillingActionPreview, CostBreakdown } from '../../components/Billing';
import { billingService, PricingConfiguration, HostBalance } from '../../services/billingService';

const CreateEventPage2: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useAppSelector((state) => state.ui);
  const { showConfirm } = useAlert();
  const { user: authUser } = useAppSelector((state) => state.auth);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [hostSearchLoading, setHostSearchLoading] = useState(false);
  const [additionalEvents, setAdditionalEvents] = useState<EventInSeriesFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSeriesAccordionOpen, setIsSeriesAccordionOpen] = useState(false);
  const [activeEventTab, setActiveEventTab] = useState<number>(0); // 0 = main event, 1+ = additional events
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isEventSeries, setIsEventSeries] = useState(false);
  const [createdEvent, setCreatedEvent] = useState<any>(null);
  const submissionInProgress = useRef(false);
  const [eventImage, setEventImage] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
  const [imageShape, setImageShape] = useState<'circle' | 'square' | 'heart' | 'star'>('square');
  // Default theme (fallback) - memoized to prevent recreation on every render
  const defaultThemes = useMemo(() => ({
    stars: {
      name: 'Starry Night',
      description: 'Animated starry background',
      background: 'url(/assets/backgrounds/gifs/background_stars.gif)',
      preview: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      fallback: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      type: 'gif'
    }
  }), []);

  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [gifUploading, setGifUploading] = useState(false);
  const [gifUploadError, setGifUploadError] = useState<string | null>(null);
  const [backgroundThemes, setBackgroundThemes] = useState<any>(defaultThemes);
  const [themesLoading, setThemesLoading] = useState(true);
  const [showThemePopup, setShowThemePopup] = useState(false);
  const gifInputRef = useRef<HTMLInputElement>(null);
  const hasSetInitialTheme = useRef(false);
  const [isGlobalGif, setIsGlobalGif] = useState(false); // Admin "for everyone" toggle
  const [saveForReuse, setSaveForReuse] = useState(true); // Host "save for reuse" toggle
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [missingAdditionalEventFields, setMissingAdditionalEventFields] = useState<Array<{ eventNumber: number; fields: string[] }>>([]);
  const [showMissingFieldsAlert, setShowMissingFieldsAlert] = useState(false);
  const [usherServicePricing, setUsherServicePricing] = useState<PricingConfiguration | null>(null);
  const [usherServiceBalance, setUsherServiceBalance] = useState<HostBalance | null>(null);
  const [usherServiceLoading, setUsherServiceLoading] = useState(false);
  const [hostHasActiveEvents, setHostHasActiveEvents] = useState<boolean>(false);
  const [isMirsalMember, setIsMirsalMember] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors }
  } = useForm<EventFormData>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      event_date: '',
      event_time: '',
      location: '',
      description: '',
      category_id: undefined,
      host_id: undefined,
      guest_limit: undefined,
      editable_until: undefined,
      editing_window_hours: undefined,
      message_template: '',
      link_expiry: undefined,
      is_series: false,
      series_name: '',
      series_description: '',
      series_overall_guest_limit: undefined,
      show_total_guests_invited: false,
      usher_service_enabled: false
    }
  });

  const watchedValues = watch();
  const isSeries = watchedValues.is_series;

  // Helper function to convert deadline date to duration format (HH:MM:SS or DD HH:MM:SS)
  const convertDateToDuration = (deadlineDate: string | undefined, eventDate: string | undefined, eventTime: string | undefined): string | undefined => {
    if (!deadlineDate || !eventDate || !eventTime) {
      return undefined;
    }

    try {
      // Parse event date and time
      const eventDateTime = new Date(`${eventDate}T${eventTime}`);
      
      // Parse deadline date (end of day - 23:59:59)
      const deadlineDateTime = new Date(deadlineDate);
      deadlineDateTime.setHours(23, 59, 59, 999);
      
      // Calculate difference in milliseconds
      const diffMs = eventDateTime.getTime() - deadlineDateTime.getTime();
      
      if (diffMs <= 0) {
        // Deadline is after or equal to event time, return undefined (invalid)
        return undefined;
      }
      
      // Convert to seconds
      const totalSeconds = Math.floor(diffMs / 1000);
      
      // Calculate days, hours, minutes, seconds
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      // Format as "DD HH:MM:SS" or "HH:MM:SS"
      if (days > 0) {
        return `${days} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } else {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
    } catch (error) {
      console.error('Error converting date to duration:', error);
      return undefined;
    }
  };

  // Clear missing fields alert when all required fields are filled
  useEffect(() => {
    if (showMissingFieldsAlert && (missingFields.length > 0 || missingAdditionalEventFields.length > 0)) {
      // Check main event fields
      const hasAllMainFields = 
        watchedValues.title && 
        watchedValues.title.trim().length >= 3 &&
        watchedValues.event_date &&
        watchedValues.event_time &&
        watchedValues.location &&
        watchedValues.location.trim().length >= 3 &&
        /^https?:\/\/.+/.test((watchedValues.location_link || '').trim()) &&
        watchedValues.category_id &&
        (authUser?.role !== 'admin' || watchedValues.host_id);
      
      // Check additional event fields
      const hasAllAdditionalFields = additionalEvents.every(event => 
        event.title && 
        event.title.trim().length >= 3 &&
        event.event_date &&
        event.event_time &&
        event.location &&
        event.location.trim().length >= 3 &&
        /^https?:\/\/.+/.test((event.location_link || '').trim())
      );
      
      if (hasAllMainFields && (additionalEvents.length === 0 || hasAllAdditionalFields)) {
        setShowMissingFieldsAlert(false);
        setMissingFields([]);
        setMissingAdditionalEventFields([]);
      }
    }
  }, [watchedValues, showMissingFieldsAlert, missingFields.length, missingAdditionalEventFields.length, additionalEvents, authUser?.role]);

  // Ensure active tab is valid when events change
  useEffect(() => {
    if (activeEventTab > additionalEvents.length) {
      setActiveEventTab(Math.max(0, additionalEvents.length));
    }
  }, [additionalEvents.length, activeEventTab]);

  // Check scroll position for shadow indicators
  const checkScrollPosition = useCallback(() => {
    if (tabsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsScrollRef.current;
      setShowLeftShadow(scrollLeft > 0);
      setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Update shadows on scroll and when tabs change
  useEffect(() => {
    checkScrollPosition();
    const scrollElement = tabsScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScrollPosition);
      // Also check on resize
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        scrollElement.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [checkScrollPosition, additionalEvents.length]);

  // Additional event management functions
  const addAdditionalEvent = () => {
    const mainEventGuestLimit = watchedValues.guest_limit;
    
    const newEvent: EventInSeriesFormData = {
      id: `event_${Date.now()}`,
      title: '',
      event_date: '',
      event_time: '',
      location: '',
      description: '',
      category_id: watchedValues.category_id,
      guest_limit: mainEventGuestLimit,
      editable_until: watchedValues.editable_until,
      message_template: watchedValues.message_template,
      usher_service_enabled: false
    };
    setAdditionalEvents(prev => [...prev, newEvent]);
    // Switch to the newly added event tab
    setActiveEventTab(additionalEvents.length + 1);
  };

  const removeAdditionalEvent = (eventId: string) => {
    const eventIndex = additionalEvents.findIndex(e => e.id === eventId);
    setAdditionalEvents(prev => prev.filter(e => e.id !== eventId));
    
    // Adjust active tab if needed
    if (activeEventTab > eventIndex + 1) {
      // If we removed an event before the active one, shift the tab index
      setActiveEventTab(activeEventTab - 1);
    } else if (activeEventTab === eventIndex + 1) {
      // If we removed the active event, switch to main event or previous event
      setActiveEventTab(Math.max(0, eventIndex));
    }
  };

  const updateAdditionalEvent = (eventId: string, field: keyof EventInSeriesFormData, value: any) => {
    setAdditionalEvents(prev => prev.map(e => 
      e.id === eventId ? { ...e, [field]: value } : e
    ));
  };

  // Calculate usher service cost breakdown (per-event)
  const calculateUsherServiceCost = (): CostBreakdown[] => {
    const costs: CostBreakdown[] = [];
    if (usherServicePricing && usherServicePricing.usher_service_fee) {
      const fee = parseFloat(usherServicePricing.usher_service_fee);
      if (fee > 0) {
        // Main event usher service
        if (watchedValues.usher_service_enabled) {
          costs.push({
            label: 'Usher Service Fee (Main Event)',
            amount: fee,
            quantity: 1,
            unitPrice: fee
          });
        }
        
        // Sub-events usher service
        additionalEvents.forEach((event, index) => {
          if (event.usher_service_enabled) {
            costs.push({
              label: `Usher Service Fee (Event ${index + 2})`,
              amount: fee,
              quantity: 1,
              unitPrice: fee
            });
          }
        });
      }
    }
    return costs;
  };

  // Calculate total event cost including setup fee and usher service fees
  const calculateTotalEventCost = (): { costs: CostBreakdown[]; setupFeeInfo: { willCharge: boolean; reason: string } } => {
    const costs: CostBreakdown[] = [];
    let setupFeeInfo = { willCharge: false, reason: '' };
    
    if (usherServicePricing) {
      const setupFee = parseFloat(usherServicePricing.setup_fee || '0');
      
      // Determine if setup fee will be charged
      if (setupFee > 0) {
        if (isMirsalMember) {
          // Mirsal members are exempt
          setupFeeInfo = {
            willCharge: false,
            reason: 'Mirsal members are waived from the setup fee'
          };
        } else if (hostHasActiveEvents) {
          // Host already has active events
          setupFeeInfo = {
            willCharge: false,
            reason: 'Setup fee is only charged for your first active event'
          };
        } else {
          // Will be charged
          setupFeeInfo = {
            willCharge: true,
            reason: ''
          };
          costs.push({
            label: 'Event Setup Fee',
            amount: setupFee,
            quantity: 1,
            unitPrice: setupFee
          });
        }
      }
      
      // Guest limit cost calculation (placeholder - not deducted)
      const guestLimit = watchedValues.guest_limit;
      if (guestLimit && guestLimit > 0) {
        const guestBaseFee = parseFloat(usherServicePricing.guest_base_fee || '0');
        if (guestBaseFee > 0) {
          const guestLimitCost = guestLimit * guestBaseFee;
          costs.push({
            label: 'Guest Limit Cost (Preview)',
            amount: guestLimitCost,
            quantity: guestLimit,
            unitPrice: guestBaseFee
          });
        }
      }
      
      // Usher service fees - group them together with a subtotal
      const usherCosts = calculateUsherServiceCost();
      if (usherCosts.length > 0) {
        // Add individual usher service fees
        costs.push(...usherCosts);
        
        // Calculate total usher service fee
        const totalUsherFee = usherCosts.reduce((sum, cost) => sum + cost.amount, 0);
        if (usherCosts.length > 1) {
          costs.push({
            label: 'Total Usher Service Fees',
            amount: totalUsherFee,
            quantity: usherCosts.length,
            unitPrice: parseFloat(usherServicePricing.usher_service_fee || '0')
          });
        }
      }
    }
    
    return { costs, setupFeeInfo };
  };

  // Test authentication function
  const testAuthentication = async () => {
    try {
      // Tokens are now in httpOnly cookies, so we just make the request
      // Cookies will be sent automatically
      const response = await fetch('/api/test/auth/', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include cookies
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        logError('Auth test failed');
        return { success: false, error: `HTTP ${response.status}: ${errorText}` };
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      logError('Auth test failed', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, error: errorMessage };
    }
  };

  // Image upload functions with security constraints
  const handleImageUpload = async (file: File) => {
    if (!file) return;

    // Security validation
    const validationResult = validateImageFile(file);
    if (!validationResult.isValid) {
      setFormError(validationResult.error);
      return;
    }

    try {
      setImageUploading(true);
      setFormError(null);

      // Test authentication first
      const authTest = await testAuthentication();
      if (!authTest || !authTest.success) {
        setFormError('Authentication failed. Please log in again.');
        return;
      }

      // Additional security: Check file content
      const isImageValid = await validateImageContent(file);
      if (!isImageValid) {
        setFormError('Invalid image file. Please select a valid image.');
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('image', file);

      // Tokens are now in httpOnly cookies, so we just make the request
      // Cookies will be sent automatically

      // Upload to backend with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 second (2 minutes) timeout

      const response = await fetch('/api/upload/image/', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        credentials: 'include', // Include cookies
        headers: {
          'X-Requested-With': 'XMLHttpRequest' // CSRF protection
        }
      });


      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `Upload failed with status: ${response.status}`;
        
        try {
          const errorData = await response.json();
          
          // Handle different error response formats
          if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData && typeof errorData === 'object') {
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            } else if (errorData.non_field_errors) {
              errorMessage = Array.isArray(errorData.non_field_errors) 
                ? errorData.non_field_errors.join(', ') 
                : errorData.non_field_errors;
            } else {
              // If it's an object with field errors, format them
              const fieldErrors = Object.entries(errorData)
                .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                .join('; ');
              errorMessage = fieldErrors || JSON.stringify(errorData);
            }
          }
        } catch (parseError) {
          logError('Failed to parse error response', parseError);
          // Try to get text response
          try {
            const textResponse = await response.text();
            errorMessage = textResponse || errorMessage;
          } catch (textError) {
            logError('Failed to get text response', textError);
          }
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Validate response
      if (!result.image_url || typeof result.image_url !== 'string') {
        throw new Error('Invalid response from server');
      }

      if (!result.image_id || typeof result.image_id !== 'string') {
        throw new Error('Invalid image ID received from server');
      }
      
      // Set the image URL and ID for preview and database storage
      setEventImage(result.image_url);
      setUploadedImageId(result.image_id);
      
      
    } catch (err: any) {
      logError('Failed to process image', err);
      
      // Handle different error response structures
      let errorMessage = 'Failed to process image. Please try again.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setImageUploading(false);
    }
  };

  // Security validation functions
  const validateImageFile = (file: File) => {
    // Allowed MIME types
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp'
    ];

    // Allowed file extensions
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    // File size limits (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB
    const minSize = 1024; // 1KB minimum

    // Check file type
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      return {
        isValid: false,
        error: `Invalid file type detected (${file.type || 'unknown'}). Only image files are allowed: JPG, JPEG, PNG, GIF, or WebP. Please select a valid image file.`
      };
    }

    // Validate filename is not empty
    if (!file.name || file.name.trim().length === 0) {
      return {
        isValid: false,
        error: 'Invalid filename: The file name is empty or contains only whitespace. Please select a file with a valid name.'
      };
    }

    // Extract file extension safely
    const lastDotIndex = file.name.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return {
        isValid: false,
        error: `Invalid filename: The file "${file.name}" does not have a file extension. Please ensure your image file has one of these extensions: .jpg, .jpeg, .png, .gif, or .webp`
      };
    }
    
    if (lastDotIndex === file.name.length - 1) {
      return {
        isValid: false,
        error: `Invalid filename: The file "${file.name}" ends with a dot but has no extension. Please ensure your image file ends with a valid extension: .jpg, .jpeg, .png, .gif, or .webp`
      };
    }

    const fileExtension = '.' + file.name.substring(lastDotIndex + 1).toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      const detectedExtension = fileExtension || 'none';
      return {
        isValid: false,
        error: `Invalid file extension: The file "${file.name}" has extension "${detectedExtension}" which is not supported. Please use one of these formats: .jpg, .jpeg, .png, .gif, or .webp`
      };
    }

    // Check file size
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        isValid: false,
        error: `File size too large: The file "${file.name}" is ${fileSizeMB} MB, which exceeds the maximum allowed size of 2 MB. Please compress or resize your image and try again.`
      };
    }

    if (file.size < minSize) {
      const fileSizeKB = (file.size / 1024).toFixed(2);
      return {
        isValid: false,
        error: `File size too small: The file "${file.name}" is ${fileSizeKB} KB, which is below the minimum required size of 1 KB. The file may be corrupted or invalid. Please select a different image file.`
      };
    }

    // Check filename for suspicious patterns (executable/script extensions)
    // This checks if the filename ends with a suspicious extension
    const suspiciousPatterns = [
      /\.(exe|bat|cmd|com|pif|scr|vbs|js|jar|php|asp|aspx|jsp)$/i,
      /\.(sh|bash|zsh|fish|ps1|psm1)$/i,
      /\.(sql|db|sqlite|mdb)$/i
    ];

    if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
      const matchedExtension = file.name.match(/\.([^.]+)$/i)?.[1] || 'unknown';
      return {
        isValid: false,
        error: `Security validation failed: The file "${file.name}" has a suspicious extension (.${matchedExtension}) that is not allowed for security reasons. Please select a valid image file (JPG, PNG, GIF, or WebP).`
      };
    }

    // Check for actual double extensions (e.g., image.jpg.exe, file.png.bat)
    // Only reject if there's a suspicious extension after a valid image extension
    const suspiciousDoubleExtensionPattern = /\.(jpg|jpeg|png|gif|webp)\.(exe|bat|cmd|com|pif|scr|vbs|js|jar|php|asp|aspx|jsp|sh|bash|zsh|fish|ps1|psm1|sql|db|sqlite|mdb)$/i;
    if (suspiciousDoubleExtensionPattern.test(file.name)) {
      const match = file.name.match(/\.(jpg|jpeg|png|gif|webp)\.([^.]+)$/i);
      const imageExt = match?.[1] || 'unknown';
      const suspiciousExt = match?.[2] || 'unknown';
      return {
        isValid: false,
        error: `Security validation failed: The file "${file.name}" appears to have a double extension (.${imageExt}.${suspiciousExt}), which is a security risk. Files with multiple extensions (especially executable or script files) are not allowed. Please select a valid image file without additional extensions.`
      };
    }

    return { isValid: true, error: null };
  };

  const validateImageContent = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Check image dimensions
        if (img.width < 100 || img.height < 100) {
          resolve(false);
          return;
        }
        
        if (img.width > 4000 || img.height > 4000) {
          resolve(false);
          return;
        }
        
        // Check aspect ratio (prevent extremely wide/tall images)
        const aspectRatio = img.width / img.height;
        if (aspectRatio > 10 || aspectRatio < 0.1) {
          resolve(false);
          return;
        }
        
        resolve(true);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };
      
      img.src = url;
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const removeImage = () => {
    setEventImage(null);
    setUploadedImageId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // GIF upload functionality
  const handleGifUpload = async (file: File) => {
    if (!file) return;

    // Validate GIF file
    if (!file.type.includes('gif')) {
      setGifUploadError('Please select a GIF file');
      return;
    }

    // Check file size (max 5MB for GIFs)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setGifUploadError('GIF file too large. Maximum size is 5MB.');
      return;
    }

    try {
      setGifUploading(true);
      setGifUploadError(null);

      // Create a clean filename
      const fileName = file.name.toLowerCase().replace(/[^a-z0-9.-]/g, '_');
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('gif', file);
      formData.append('filename', fileName);
      
      // Add toggle parameters
      // is_global: only for admins, makes GIF available to all hosts
      if (authUser?.role === 'admin') {
        formData.append('is_global', isGlobalGif.toString());
      }
      
      // save_for_reuse: for all users, determines if GIF is saved for reuse or event-specific
      formData.append('save_for_reuse', saveForReuse.toString());
      
      // If not saving for reuse and we have an event being created, we'll link it after event creation
      // For now, we'll upload it as a reusable GIF and link it later if needed

      // Tokens are now in httpOnly cookies, so we just make the request
      // Cookies will be sent automatically

      // Upload to backend
      const response = await fetch('/api/upload/gif/', {
        method: 'POST',
        body: formData,
        credentials: 'include', // Include cookies
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        let errorMessage = `Upload failed with status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          const textResponse = await response.text();
          errorMessage = textResponse || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const uploadResult = await response.json();
      
      // Automatically select the uploaded GIF with correct key format
      if (uploadResult.gif_id) {
        setSelectedTheme(`gif_${uploadResult.gif_id}`);
      }
      
      // Show success message based on selection
      let successMsg = `GIF "${fileName}" uploaded successfully and selected!`;
      if (authUser?.role === 'admin' && isGlobalGif) {
        successMsg += ' It\'s now available to all hosts.';
      } else if (saveForReuse) {
        successMsg += ' It\'s saved in your library for reuse in other events.';
      } else {
        successMsg += ' It\'s linked to this event only.';
      }
      setSuccess(successMsg);
      
      // Reset toggles
      setIsGlobalGif(false);
      setSaveForReuse(true);
      
      // Reload themes to include the new GIF
      await loadThemes();
      
      // Clear the input
      if (gifInputRef.current) {
        gifInputRef.current.value = '';
      }


    } catch (error: any) {
      logError('GIF upload failed', error);
      
      // Handle different error response structures
      let errorMessage = 'Failed to upload GIF. Please try again.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setGifUploadError(errorMessage);
    } finally {
      setGifUploading(false);
    }
  };

  // Handle GIF deletion
  const handleDeleteGif = async (themeKey: string, gifId?: string, filename?: string) => {
    try {
      // Tokens are now in httpOnly cookies, so we just make the request
      // Cookies will be sent automatically

      // Use gif_id if available (preferred), otherwise fall back to filename for backward compatibility
      const requestBody: any = {};
      if (gifId) {
        requestBody.gif_id = gifId;
      } else if (filename) {
        requestBody.filename = filename;
      } else {
        setFormError('GIF ID or filename is required');
        return;
      }

      const response = await fetch('/api/gifs/delete/', {
        method: 'DELETE',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccess('GIF deleted successfully!');
          // If the deleted theme was selected, switch to null (will be set to black by loadThemes)
          if (selectedTheme === themeKey) {
            setSelectedTheme(null);
          }
          // Reload themes to update the list
          await loadThemes();
        } else {
          setFormError(result.message || 'Failed to delete GIF');
        }
      } else {
        const errorData = await response.json();
        setFormError(errorData.message || 'Failed to delete GIF');
      }
    } catch (error: any) {
      setFormError(error.message || 'Failed to delete GIF. Please try again.');
    }
  };

  const handleGifFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleGifUpload(file);
    }
  };

  // Load themes from API
  const loadThemes = useCallback(async () => {
    try {
      setThemesLoading(true);
      
      // Tokens are now in httpOnly cookies, so we just make the request
      // Cookies will be sent automatically

      const response = await fetch('/api/gifs/list/', {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Convert API response to theme format
          // API now returns themes array with both GIFs and colors
          const themes: any = { ...defaultThemes };
          
          // Process themes from API (includes both GIFs and colors)
          if (result.themes && Array.isArray(result.themes)) {
            result.themes.forEach((theme: any) => {
              if (theme.type === 'gif') {
                themes[theme.key] = {
                  name: theme.name,
                  description: theme.description,
                  background: `url(${theme.url})`,
                  preview: `url(${theme.url})`,
                  fallback: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  type: 'gif',
                  id: theme.id,
                  filename: theme.filename,
                  url: theme.url,
                  is_global: theme.is_global,
                  can_delete: theme.can_delete
                };
              } else if (theme.type === 'color') {
                themes[theme.key] = {
                  name: theme.name,
                  description: theme.description,
                  background: theme.color_code,
                  preview: theme.color_code,
                  fallback: theme.color_code,
                  type: 'color',
                  id: theme.id,
                  color_code: theme.color_code,
                  is_global: true,
                  can_delete: false
                };
              }
            });
          }
          
          // Backward compatibility: also process result.gifs if present
          if (result.gifs && Array.isArray(result.gifs)) {
          result.gifs.forEach((gif: any) => {
              if (!themes[gif.key]) {
            themes[gif.key] = {
              name: gif.name,
              description: gif.description,
              background: `url(${gif.url})`,
              preview: `url(${gif.url})`,
              fallback: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              type: 'gif',
                  id: gif.id,
              filename: gif.filename,
                  url: gif.url,
                  is_global: gif.is_global,
                  can_delete: gif.can_delete
            };
              }
          });
          }
          
          setBackgroundThemes(themes);
          
          // Set black color theme as default only on initial load
          // Don't change the theme if user has already selected one or if we've already set initial theme
          if (!hasSetInitialTheme.current && Object.keys(themes).length > 0) {
            // First, try to find the black color theme
            const blackThemeKey = Object.keys(themes).find(key => {
              const theme = themes[key];
              return theme?.type === 'color' && theme?.name?.toLowerCase() === 'black';
            });
            
            if (blackThemeKey) {
              setSelectedTheme(blackThemeKey);
            } else {
              // If no black theme, find the first color theme
              const firstColorKey = Object.keys(themes).find(key => {
                const theme = themes[key];
                return theme?.type === 'color';
              });
              
              if (firstColorKey) {
                setSelectedTheme(firstColorKey);
              } else {
                // Fallback to stars if no colors exist
                const firstKey = Object.keys(themes).find(k => k !== 'stars') || 'stars';
                setSelectedTheme(firstKey);
              }
            }
            hasSetInitialTheme.current = true;
          }
        }
      } else {
        logError('Failed to load themes, using defaults');
        setBackgroundThemes(defaultThemes);
      }
    } catch (error) {
      logError('Error loading themes', error);
      setBackgroundThemes(defaultThemes);
    } finally {
      setThemesLoading(false);
    }
  }, [defaultThemes]);

  // Load categories, hosts, themes, and billing data
  useEffect(() => {
    const loadData = async () => {
      try {
        const categoriesData = await categoryAPI.getCategories();
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        
        // Load hosts if user is admin
        if (authUser?.role === 'admin') {
          const hostsData = await usersAPI.getHosts();
          setHosts(Array.isArray(hostsData) ? hostsData : []);
        }
        
        // Load themes
        await loadThemes();
        
        // Load billing data for cost preview
        try {
          const [pricing, balance, events, profile] = await Promise.all([
            billingService.getCurrentPricing().catch(() => null),
            billingService.getMyBalance().catch(() => null),
            eventAPI.getEvents({}, 1, 100, true).catch(() => null), // Fetch events to check for active ones
            authUser ? (async () => {
              try {
                const authAPI = (await import('../../services/api')).authAPI;
                const response = await authAPI.getProfile();
                return response.data.data;
              } catch {
                return null;
              }
            })() : Promise.resolve(null)
          ]);
          setUsherServicePricing(pricing);
          setUsherServiceBalance(balance);
          
          // Check if host has existing active events
          if (events?.events) {
            const activeEvents = events.events.filter(
              (e: Event) => e.status === 'active' || e.status === 'rescheduled'
            );
            setHostHasActiveEvents(activeEvents.length > 0);
          }
          
          // Check if user is Mirsal member from profile
          if (profile?.is_mirsal_member !== undefined) {
            setIsMirsalMember(profile.is_mirsal_member);
          }
        } catch (error) {
          logError('Failed to load billing data', error);
        }
      } catch (error) {
        logError('Failed to load data', error);
        setCategories([]);
        setHosts([]);
        setBackgroundThemes(defaultThemes);
      }
    };

    loadData();
  }, [authUser?.role, loadThemes, defaultThemes]);

  // Host search function
  const handleHostSearch = async (searchTerm: string) => {
    if (authUser?.role !== 'admin') return;
    
    setHostSearchLoading(true);
    
    try {
      const hostsData = await usersAPI.getHosts(searchTerm);
      setHosts(Array.isArray(hostsData) ? hostsData : []);
    } catch (error: any) {
      logError('Error searching hosts', error);
      // Keep existing hosts on error
    } finally {
      setHostSearchLoading(false);
    }
  };


  // Handle navigation to events page after success popup
  const handleSuccessNavigation = () => {
    setShowSuccessPopup(false);
    setSuccessMessage('');
    setIsEventSeries(false);
    setCreatedEvent(null);
    
    // Navigate based on user role
    const basePath = authUser?.role === 'admin' ? '/admin' : '/host';
    navigate(`${basePath}/events`);
  };

  // Handle navigation to view the created event
  const handleViewCreatedEvent = async () => {
    if (!createdEvent) return;
    
    setShowSuccessPopup(false);
    setSuccessMessage('');
    setIsEventSeries(false);
    
    // For series, navigate to the main event in the series
    if (isEventSeries) {
      try {
        let mainEvent = null;
        
        // First, check if events are already in the response
        if (createdEvent.events && createdEvent.events.length > 0) {
          // Find the main event (marked as is_main_event) or use the first one
          mainEvent = createdEvent.events.find((e: any) => e.is_main_event === true) || createdEvent.events[0];
        } else if (createdEvent.id) {
          // If events are not in response, fetch them from the series
          const seriesEventsResponse = await seriesAPI.getSeriesEvents(createdEvent.id);
          
          // Handle different response structures
          // Backend returns: { success: true, data: { series: {...}, events: [...] } }
          let events: any[] = [];
          if (Array.isArray(seriesEventsResponse)) {
            events = seriesEventsResponse;
          } else if (seriesEventsResponse.data && seriesEventsResponse.data.events) {
            events = seriesEventsResponse.data.events;
          } else if (seriesEventsResponse.events) {
            events = seriesEventsResponse.events;
          } else if (seriesEventsResponse.data && Array.isArray(seriesEventsResponse.data)) {
            events = seriesEventsResponse.data;
          }
          
          if (events.length > 0) {
            // Find the main event (marked as is_main_event) or use the first one
            mainEvent = events.find((e: any) => e.is_main_event === true) || events[0];
          }
        }
        
        if (mainEvent) {
          const viewUrl = generateEventViewUrl(mainEvent.id, mainEvent.title, mainEvent.event_date, authUser?.role, mainEvent.slug);
          navigate(viewUrl);
        } else {
          // Fallback to events page if no events found
          const basePath = authUser?.role === 'admin' ? '/admin' : '/host';
          navigate(`${basePath}/events`);
        }
      } catch (error) {
        logError('Failed to fetch series events for navigation', error);
        // Fallback to events page on error
        const basePath = authUser?.role === 'admin' ? '/admin' : '/host';
        navigate(`${basePath}/events`);
      }
    } else {
      // For single events, generate the view URL
      const viewUrl = generateEventViewUrl(createdEvent.id, createdEvent.title, createdEvent.event_date, authUser?.role, createdEvent.slug);
      navigate(viewUrl);
    }
    
    setCreatedEvent(null);
  };

  // Function to validate all required fields and return missing ones
  const validateRequiredFields = async (data: EventFormData): Promise<string[]> => {
    const missing: string[] = [];
    
    // Trigger validation for all fields
    await trigger();
    
    // Check required fields
    if (!data.title || data.title.trim().length < 3) {
      missing.push('Event Title');
    }
    if (!data.event_date) {
      missing.push('Event Date');
    }
    if (!data.event_time) {
      missing.push('Event Time');
    }
    if (!data.location || data.location.trim().length < 3) {
      missing.push('Location');
    }
    if (!data.location_link || !/^https?:\/\/.+/.test(data.location_link.trim())) {
      missing.push('Location Link');
    }
    if (!data.category_id) {
      missing.push('Category');
    }
    if (authUser?.role === 'admin' && !data.host_id) {
      missing.push('Host Assignment');
    }
    
    return missing;
  };

  // Function to validate additional events in the series
  const validateAdditionalEvents = (): Array<{ eventNumber: number; fields: string[] }> => {
    const errors: Array<{ eventNumber: number; fields: string[] }> = [];
    const mainEventDate = watchedValues.event_date;
    
    additionalEvents.forEach((event, index) => {
      const missing: string[] = [];
      
      // Check required fields (same as main event validation)
      if (!event.title || event.title.trim().length < 3) {
        missing.push('Event Title');
      }
      if (!event.event_date) {
        missing.push('Event Date');
      }
      if (!event.event_time) {
        missing.push('Event Time');
      }
      if (!event.location || event.location.trim().length < 3) {
        missing.push('Location');
      }
      if (!event.location_link || !/^https?:\/\/.+/.test(event.location_link.trim())) {
        missing.push('Location Link');
      }
      
      // Validate that sub-event date is not after main event date
      // Sub-events can be on the same day, but not after
      if (event.event_date && mainEventDate) {
        const subEventDate = new Date(event.event_date);
        const mainDate = new Date(mainEventDate);
        subEventDate.setHours(0, 0, 0, 0);
        mainDate.setHours(0, 0, 0, 0);
        if (subEventDate > mainDate) {
          missing.push('Event Date (cannot be after main event)');
        }
      }
      
      // If any fields are missing, add to errors
      // Event number is 1-indexed, where Event 1 is main event, Event 2 is first additional event, etc.
      if (missing.length > 0) {
        errors.push({
          eventNumber: index + 2, // +2 because Event 1 is the main event
          fields: missing
        });
      }
    });
    
    return errors;
  };

  const handleCreateEvent = async (data: EventFormData) => {
    // Prevent multiple submissions
    if (submissionInProgress.current) {
      return;
    }

    // Validate all required fields for main event
    const missing = await validateRequiredFields(data);
    
    // Validate additional events if creating a series
    const additionalEventErrors = additionalEvents.length > 0 ? validateAdditionalEvents() : [];
    
    if (missing.length > 0 || additionalEventErrors.length > 0) {
      setMissingFields(missing);
      setMissingAdditionalEventFields(additionalEventErrors);
      setShowMissingFieldsAlert(true);
      
      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Auto-hide alert after 5 seconds
      setTimeout(() => {
        setShowMissingFieldsAlert(false);
      }, 5000);
      
      return;
    }

    // Clear any previous missing fields alert
    setMissingFields([]);
    setMissingAdditionalEventFields([]);
    setShowMissingFieldsAlert(false);

    try {
      submissionInProgress.current = true;
      setLoading(true);
      setFormError(null);

      if (additionalEvents.length > 0) {
        // Convert deadline date to duration format for main event
        const mainEventEditableUntil = convertDateToDuration(
          data.editable_until,
          data.event_date,
          data.event_time
        );

        // Create as series with multiple events
        const seriesData = {
          name: data.title,
          description: data.series_description || data.description,
          category_id: data.category_id,
          host_id: authUser?.role === 'admin' ? data.host_id : undefined,
          overall_guest_limit: undefined,
          image_url: eventImage || undefined, // Include uploaded image for series
          image_id: uploadedImageId || undefined, // Include image ID for database reference
          image_shape: imageShape, // Include selected image shape
          background_theme: selectedTheme || undefined, // Include selected background theme
          events: [
            // First event (the main form data)
            {
              id: `main_event_${Date.now()}`,
              title: data.title,
              event_date: data.event_date,
              event_time: data.event_time,
              location: data.location,
              description: data.description,
              guest_limit: data.guest_limit ? (typeof data.guest_limit === 'string' ? parseInt(data.guest_limit) : data.guest_limit) : undefined,
              editable_until: mainEventEditableUntil,
              message_template: data.message_template,
              image_url: eventImage || undefined,
              image_id: uploadedImageId || undefined,
              image_shape: imageShape,
              background_theme: selectedTheme,
              editing_window_hours: data.editing_window_hours ? parseInt(data.editing_window_hours.toString()) : undefined,
              location_link: data.location_link || undefined, // Include location link
              website_link: data.website_link || undefined, // Include website link
              dress_code: data.dress_code || undefined, // Include dress code
              usher_service_enabled: data.usher_service_enabled || false // Include usher service for main event
            },
            // Additional events
            ...additionalEvents.map(event => {
              // Convert deadline date to duration format for each additional event
              const eventEditableUntil = event.editable_until 
                ? convertDateToDuration(event.editable_until, event.event_date, event.event_time)
                : mainEventEditableUntil;
              
              return {
                ...event,
                editable_until: eventEditableUntil,
                message_template: event.message_template || data.message_template,
              image_url: eventImage || undefined,
              image_id: uploadedImageId || undefined,
              image_shape: imageShape,
              background_theme: selectedTheme,
              editing_window_hours: data.editing_window_hours ? parseInt(data.editing_window_hours.toString()) : undefined,
              guest_limit: event.guest_limit ? (typeof event.guest_limit === 'string' ? parseInt(event.guest_limit) : event.guest_limit) : undefined,
              location_link: event.location_link || undefined, // Include location link
              website_link: event.website_link || undefined, // Include website link
              dress_code: event.dress_code || undefined, // Include dress code
              category_id: event.category_id || undefined, // Include category ID
              usher_service_enabled: event.usher_service_enabled || false // Include usher service for this sub-event
            };
          })
          ]
        };
        
        const createdSeries = await seriesAPI.createSeries(seriesData);
        
        setCreatedEvent(createdSeries);
        setSuccessMessage('Event series created successfully!');
        setIsEventSeries(true);
        setShowSuccessPopup(true);
      } else {
        // Create single event
        // Convert deadline date to duration format if provided
        const editableUntilDuration = convertDateToDuration(
          data.editable_until,
          data.event_date,
          data.event_time
        );

        const eventData = {
          title: data.title,
          event_date: data.event_date,
          event_time: data.event_time,
          location: data.location,
          description: data.description,
          category_id: data.category_id,
          host_id: authUser?.role === 'admin' ? data.host_id : undefined,
          guest_limit: data.guest_limit ? (typeof data.guest_limit === 'string' ? parseInt(data.guest_limit) : data.guest_limit) : undefined,
          editable_until: editableUntilDuration,
          message_template: data.message_template,
          link_expiry: data.link_expiry,
          image_url: eventImage || undefined, // Include uploaded image
          image_id: uploadedImageId || undefined, // Include image ID for database reference
          image_shape: imageShape, // Include selected image shape
          background_theme: selectedTheme || undefined, // Include selected background theme
          editing_window_hours: data.editing_window_hours ? parseInt(data.editing_window_hours.toString()) : undefined,
          location_link: data.location_link || undefined, // Include location link
          website_link: data.website_link || undefined, // Include website link
          dress_code: data.dress_code || undefined, // Include dress code
          series_id: data.series_id || undefined, // Include series_id if present
          usher_service_enabled: data.usher_service_enabled || false, // Include usher service
        };
        
        const createdEventData = await eventAPI.createEvent(eventData);
        
        setCreatedEvent(createdEventData);
        setSuccessMessage('Event created successfully!');
        setIsEventSeries(false);
        setShowSuccessPopup(true);
      }
    } catch (err: any) {
      logError('Failed to create event', err);
      
      // Handle different error response structures
      let errorMessage = 'Failed to create event. Please try again.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setLoading(false);
      submissionInProgress.current = false;
    }
  };

  // Get the selected theme data
  const selectedThemeData = selectedTheme ? backgroundThemes[selectedTheme] : null;
  const isColorTheme = selectedThemeData?.type === 'color';
  
  // Build style object based on theme type
  // Default to black if no theme is selected
  const backgroundStyle: React.CSSProperties = !selectedThemeData
    ? {
        backgroundColor: '#000000'
      }
    : isColorTheme
    ? {
        // For static colors, use backgroundColor
        backgroundColor: selectedThemeData?.color_code || selectedThemeData?.background || '#000000',
      }
    : {
        // For GIFs, use backgroundImage
        backgroundImage: selectedThemeData
          ? `${selectedThemeData.background}, ${selectedThemeData.fallback}`
          : 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };

  return (
    <div className="min-h-screen relative overflow-hidden" style={backgroundStyle}>
      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-6">
        {/* Main Layout: Centered Form, Sidebar on right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Spacer column for centering */}
          <div className="hidden lg:block lg:col-span-2"></div>
          
          {/* Centered Form Container */}
          <div className={`lg:col-span-7 rounded-lg p-6 backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-gray-900/80 border border-gray-700' 
              : 'bg-white/90 border border-gray-200'
          }`}>
            <form onSubmit={handleSubmit(handleCreateEvent)} className="space-y-6">
                {/* Title and Category Row */}
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    {/* Event Title - More Prominent */}
                    <div className="flex-[2] w-full">
                      <label className={`block text-sm font-semibold mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Event Title <span className="text-red-500">*</span>
                      </label>
                      <Input
                        variant="mirsal"
                        className={`text-xl font-bold w-full ${
                          theme === 'dark' 
                            ? 'bg-gray-800/50 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Enter event title..."
                        {...register('title', { 
                          required: 'Title is required',
                          minLength: { value: 3, message: 'Title must be at least 3 characters' }
                        })}
                        error={errors.title?.message}
                      />
                    </div>
                    
                    {/* Category Dropdown */}
                    <div className="w-full sm:w-48">
                      <CategoryDropdown
                        options={categories}
                        value={watchedValues.category_id}
                        onChange={(value) => {
                          setValue('category_id', value, { shouldValidate: true });
                          if (errors.category_id) {
                            clearErrors('category_id');
                          }
                        }}
                        placeholder="Select category..."
                        label="Category"
                        error={errors.category_id?.message}
                        required
                      />
                    </div>
                  </div>
                </div>
                {/* Required Fields Section */}
                <div className="space-y-6">
                  <div>
                    <h3 className={`text-base font-semibold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Required Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DatePicker
                        label="Event Date"
                        value={watchedValues.event_date}
                        onChange={(value) => {
                          setValue('event_date', value);
                          // Trigger validation
                          if (value) {
                            const selectedDate = new Date(value);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            if (selectedDate < today) {
                              setError('event_date', { 
                                type: 'manual', 
                                message: 'Event date cannot be in the past' 
                              });
                            } else {
                              // Clear the error if date is valid
                              clearErrors('event_date');
                            }
                          }
                        }}
                        minDate={new Date().toISOString().split('T')[0]}
                        placeholder="Select event date"
                        error={errors.event_date?.message}
                        helpText="Choose when your event will take place"
                        required
                      />
                      
                      <Input
                        label="Location"
                        placeholder="e.g., Grand Hall, Amman"
                        required
                        variant="mirsal"
                        {...register('location', { 
                          required: 'Location is required',
                          minLength: { value: 3, message: 'Location must be at least 3 characters' }
                        })}
                        error={errors.location?.message}
                        helpText="For WhatsApp invites, use Venue, City (comma-separated), e.g. Grand Hall, Amman — otherwise the message may show “in -”."
                        leftIcon={<MapPin className="h-4 w-4" />}
                      />
                      
                      <TimePicker
                        label="Event Time"
                        value={watchedValues.event_time}
                        onChange={(value) => {
                          setValue('event_time', value);
                          // Validate that time is not in the past if date is today
                          if (watchedValues.event_date) {
                            const selectedDate = new Date(watchedValues.event_date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            selectedDate.setHours(0, 0, 0, 0);
                            
                            if (selectedDate.getTime() === today.getTime() && value) {
                              const now = new Date();
                              const [selectedHours, selectedMinutes] = value.split(':').map(Number);
                              const selectedTime = new Date();
                              selectedTime.setHours(selectedHours, selectedMinutes, 0, 0);
                              
                              if (selectedTime < now) {
                                setError('event_time', {
                                  type: 'manual',
                                  message: 'Event time cannot be in the past'
                                });
                              } else {
                                clearErrors('event_time');
                              }
                            } else {
                              clearErrors('event_time');
                            }
                          }
                        }}
                        placeholder="Select event time"
                        error={errors.event_time?.message}
                        helpText="Choose when your event will start"
                        required
                        minTime={
                          watchedValues.event_date ? (() => {
                            const selectedDate = new Date(watchedValues.event_date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            selectedDate.setHours(0, 0, 0, 0);
                            
                            // If selected date is today, set minTime to current time
                            if (selectedDate.getTime() === today.getTime()) {
                              const now = new Date();
                              const hours = now.getHours().toString().padStart(2, '0');
                              const minutes = now.getMinutes().toString().padStart(2, '0');
                              return `${hours}:${minutes}`;
                            }
                            return undefined;
                          })() : undefined
                        }
                      />
                      
                      {/* Host Selection (Admin only) */}
                      {authUser?.role === 'admin' ? (
                        <div>
                          <SearchableSelect
                            options={hosts.length > 0 ? hosts.map(host => ({
                              id: host.id,
                              label: host.get_full_name || `${host.first_name} ${host.last_name}`,
                              subtitle: host.email,
                              value: host.id
                            })) : [
                              { id: 'no-hosts', label: 'No hosts available', subtitle: 'Please contact admin', value: '' }
                            ]}
                            value={watchedValues.host_id}
                            onChange={(value) => setValue('host_id', value)}
                            onSearch={handleHostSearch}
                            placeholder="Search for a host..."
                            label="Assign Host"
                            error={errors.host_id?.message}
                            helpText="Choose which host will manage this event"
                            loading={hostSearchLoading}
                            required
                          />
                        </div>
                      ) : (
                        <div></div>
                      )}
                    </div>
                  </div>

                  {/* Additional information */}
                  <div>
                    <h3 className={`text-base font-semibold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Additional Information</h3>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Location Link"
                          placeholder="e.g., https://maps.google.com/..."
                          type="url"
                          variant="mirsal"
                          required
                          {...register('location_link', {
                            required: 'Location link is required',
                            pattern: {
                              value: /^https?:\/\/.+/,
                              message: 'Please enter a valid URL starting with http:// or https://'
                            }
                          })}
                          error={errors.location_link?.message}
                          leftIcon={<LinkIcon className="h-4 w-4" />}
                          helpText="Google Maps (or similar) link for this venue"
                        />
                        <Input
                          label="Website Link"
                          placeholder="e.g., https://event-website.com"
                          type="url"
                          variant="mirsal"
                          {...register('website_link', {
                            pattern: {
                              value: /^https?:\/\/.+/,  
                              message: 'Please enter a valid URL starting with http:// or https://'
                            }
                          })}
                          error={errors.website_link?.message}
                          leftIcon={<Globe className="h-4 w-4" />}
                          helpText="Link to event website"
                        />
                        <Input
                          label="Dress Code"
                          placeholder="e.g., Formal, Casual, Black Tie"
                          variant="mirsal"
                          {...register('dress_code')}
                          error={errors.dress_code?.message}
                          leftIcon={<Shirt className="h-4 w-4" />}
                          helpText="Dress code information for the event"
                        />
                        <Input
                          label="Guest Limit"
                          placeholder="e.g., 50 (leave blank for unlimited)"
                          type="number"
                          min="1"
                          max="10000"
                          variant="mirsal"
                          {...register('guest_limit', {
                            validate: (value) => {
                              if (value && (value < 1 || value > 10000)) {
                                return 'Guest limit must be between 1 and 10,000';
                              }
                              return true;
                            }
                          })}
                          error={errors.guest_limit?.message}
                          leftIcon={<Users className="h-4 w-4" />}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Event Description (Optional)
                  </label>
                  <textarea
                    placeholder="Add a description of your event"
                    className={`w-full p-4 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green transition-all ${
                      theme === 'dark'
                        ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    rows={4}
                    {...register('description')}
                  />
                </div>

                {/* Communication Settings */}
                <div className={`border-2 rounded-lg p-6 ${
                  theme === 'dark' ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'
                }`}>
                  <h4 className={`text-base font-semibold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Communication Settings</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DatePicker
                      label="Link Expiry"
                      value={watchedValues.link_expiry}
                      onChange={(value) => setValue('link_expiry', value)}
                      minDate={new Date().toISOString().split('T')[0]}
                      placeholder="Select expiry date"
                      helpText="When should invitation links expire?"
                    />
                    <DatePicker
                      label="Response Deadline"
                      value={watchedValues.editable_until}
                      onChange={(value) => setValue('editable_until', value)}
                      minDate={new Date().toISOString().split('T')[0]}
                      maxDate={watchedValues.event_date || undefined}
                      placeholder="Select deadline date"
                      helpText={watchedValues.event_date ? "Deadline must be on or before the event date. Leave empty for no deadline." : "When should guests respond by? Leave empty for no deadline."}
                    />
                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Event Editing Window
                      </label>
                      <select
                        {...register('editing_window_hours', { 
                          setValueAs: (v) => v === '' ? undefined : parseInt(v)
                        })}
                        className={`block w-full px-3 py-2 rounded-md border shadow-sm focus:outline-none focus:ring-mirsal-green focus:border-mirsal-green transition-all ${
                          theme === 'dark' 
                            ? 'border-gray-600 bg-gray-800 text-white' 
                            : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      >
                        <option value="">None (no restriction)</option>
                        <option value="1">1 hour before</option>
                        <option value="6">6 hours before</option>
                        <option value="12">12 hours before</option>
                        <option value="24">24 hours before</option>
                        <option value="48">48 hours before</option>
                        <option value="72">72 hours before</option>
                        <option value="168">1 week (168 hours) before</option>
                      </select>
                      <p className={`text-xs mt-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        How long before the event starts can you still edit event details?
                      </p>
                    </div>
                  </div>
                </div>

                {/* Usher Service Section - Main Event Only */}
                <div className={`border-2 rounded-lg p-6 ${
                  theme === 'dark' ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <h4 className={`text-base font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Usher Service (Main Event)
                      </h4>
                      <p className={`text-sm mt-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Enable usher service to manage ushers for the main event. The fee will be charged per event.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={watchedValues.usher_service_enabled || false}
                        onChange={async (e) => {
                          setValue('usher_service_enabled', e.target.checked);
                          // Fetch billing data when toggled (for cost preview in sidebar)
                          setUsherServiceLoading(true);
                          try {
                            const [pricing, balance] = await Promise.all([
                              billingService.getCurrentPricing().catch(() => null),
                              billingService.getMyBalance().catch(() => null)
                            ]);
                            setUsherServicePricing(pricing);
                            setUsherServiceBalance(balance);
                          } catch (error) {
                            logError('Failed to fetch billing data', error);
                          } finally {
                            setUsherServiceLoading(false);
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 rounded-full peer ${
                        watchedValues.usher_service_enabled
                          ? 'bg-mirsal-green' 
                          : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'
                      } transition-colors`}>
                        <div className={`mt-0.5 ml-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                          watchedValues.usher_service_enabled ? 'translate-x-5' : ''
                        }`} />
                      </div>
                    </label>
                  </div>
                </div>

                {/* Series Settings Accordion */}
                <div className={`border-2 rounded-lg ${
                  theme === 'dark' ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'
                }`}>
              {/* Accordion Header */}
              <button
                type="button"
                onClick={() => setIsSeriesAccordionOpen(!isSeriesAccordionOpen)}
                className={`w-full flex items-center justify-between p-4 text-left hover:bg-opacity-80 transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100/50'
                }`}
              >
                <div>
                  <h4 className={`text-xs font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Want to make your event as series?
                  </h4>
                  {additionalEvents.length > 0 && (
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {additionalEvents.length} additional event{additionalEvents.length !== 1 ? 's' : ''} added
                    </p>
                  )}
                </div>
                <ChevronDown 
                  className={`h-4 w-4 transition-transform ${
                    isSeriesAccordionOpen ? 'rotate-180' : ''
                  } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} 
                />
              </button>

              {/* Accordion Content */}
              {isSeriesAccordionOpen && (
                <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                      <div>
                        <h5 className={`text-xs font-semibold mb-1 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Create Event Series</h5>
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          Transform your event into a series by adding more related events
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={addAdditionalEvent}
                        variant="primary"
                        size="sm"
                        className="mt-2 sm:mt-0"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Event to Series
                      </Button>
                    </div>

                    {additionalEvents.length === 0 ? (
                      <div className="text-center py-6">
                        <Calendar className={`h-8 w-8 mx-auto mb-3 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <h6 className={`text-xs font-medium mb-2 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Single Event Mode</h6>
                        <p className={`text-xs max-w-md mx-auto ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Your event will be created as a standalone event. Click "Add Event to Series" to create multiple related events.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Tab Navigation with Scroll Shadows */}
                        <div className="relative">
                          {/* Left Shadow */}
                          {showLeftShadow && (
                            <div 
                              className={`absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-10 ${
                                theme === 'dark' 
                                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-800/20 to-transparent' 
                                  : 'bg-gradient-to-r from-gray-50 via-gray-50/50 to-transparent'
                              }`}
                            />
                          )}
                          
                          {/* Right Shadow */}
                          {showRightShadow && (
                            <div 
                              className={`absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-10 ${
                                theme === 'dark' 
                                  ? 'bg-gradient-to-l from-gray-800/30 via-gray-800/20 to-transparent' 
                                  : 'bg-gradient-to-l from-gray-50 via-gray-50/50 to-transparent'
                              }`}
                            />
                          )}

                          <div 
                            ref={tabsScrollRef}
                            className={`flex items-center gap-2 overflow-x-auto pb-2 border-b scrollbar-hide ${
                              theme === 'dark' ? 'border-gray-700' : 'border-gray-300'
                            }`}
                          >
                          {/* Main Event Tab */}
                          <button
                            type="button"
                            onClick={() => setActiveEventTab(0)}
                            className={`flex-shrink-0 px-4 py-2 rounded-t-lg transition-all relative ${
                              activeEventTab === 0
                                ? theme === 'dark'
                                  ? 'bg-gray-800 border-t border-l border-r border-mirsal-green text-white shadow-[0_0_8px_rgba(34,197,94,0.3)]'
                                  : 'bg-white border-t border-l border-r border-mirsal-green text-gray-900 shadow-[0_0_8px_rgba(34,197,94,0.2)]'
                                : theme === 'dark'
                                  ? 'bg-gray-900/50 text-gray-400 hover:text-gray-300 border-t border-l border-r border-transparent'
                                  : 'bg-gray-100 text-gray-600 hover:text-gray-900 border-t border-l border-r border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium">Main Event</span>
                              {watchedValues.title && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                                }`}>
                                  {watchedValues.title.length > 15 ? watchedValues.title.substring(0, 15) + '...' : watchedValues.title}
                                </span>
                              )}
                            </div>
                          </button>

                          {/* Additional Event Tabs */}
                          {additionalEvents.map((event, index) => (
                            <button
                              key={event.id}
                              type="button"
                              onClick={() => setActiveEventTab(index + 1)}
                              className={`flex-shrink-0 px-4 py-2 rounded-t-lg transition-all relative ${
                                activeEventTab === index + 1
                                  ? theme === 'dark'
                                    ? 'bg-gray-800 border-t border-l border-r border-mirsal-green text-white shadow-[0_0_8px_rgba(34,197,94,0.3)]'
                                    : 'bg-white border-t border-l border-r border-mirsal-green text-gray-900 shadow-[0_0_8px_rgba(34,197,94,0.2)]'
                                  : theme === 'dark'
                                    ? 'bg-gray-900/50 text-gray-400 hover:text-gray-300 border-t border-l border-r border-transparent'
                                    : 'bg-gray-100 text-gray-600 hover:text-gray-900 border-t border-l border-r border-transparent'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium">Event {index + 2}</span>
                                {event.title && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                                  }`}>
                                    {event.title.length > 12 ? event.title.substring(0, 12) + '...' : event.title}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeAdditionalEvent(event.id);
                                  }}
                                  className={`ml-1 p-0.5 rounded hover:bg-opacity-20 ${
                                    theme === 'dark' 
                                      ? 'hover:bg-red-500 text-red-400' 
                                      : 'hover:bg-red-100 text-red-600'
                                  }`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </button>
                          ))}
                          </div>
                        </div>

                        {/* Active Tab Full Form */}
                        <div className={`border rounded-lg p-4 ${
                          theme === 'dark' 
                            ? 'bg-gray-900/50 border-gray-600' 
                            : 'bg-white border-gray-300'
                        }`}>
                          {activeEventTab === 0 ? (
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <h6 className={`text-sm font-semibold ${
                                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  Main Event Details
                                </h6>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  theme === 'dark' ? 'bg-mirsal-green/20 text-mirsal-green' : 'bg-mirsal-green/10 text-mirsal-green'
                                }`}>
                                  Currently Editing
                                </span>
                              </div>
                              <p className={`text-xs mb-4 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                Edit the main event details above in the main form section.
                              </p>
                            </div>
                          ) : (
                            (() => {
                              const event = additionalEvents[activeEventTab - 1];
                              if (!event) return null;
                              
                              return (
                                <div>
                                  <div className="flex items-center justify-between mb-4">
                                    <h6 className={`text-sm font-semibold ${
                                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                      Event {activeEventTab + 1} Details
                                    </h6>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      theme === 'dark' ? 'bg-mirsal-green/20 text-mirsal-green' : 'bg-mirsal-green/10 text-mirsal-green'
                                    }`}>
                                      Currently Editing
                                    </span>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                      <label className={`block text-sm font-semibold mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                      }`}>
                                        Event Title <span className="text-red-500">*</span>
                                      </label>
                                      <Input
                                        variant="mirsal"
                                        placeholder="Enter event title"
                                        value={event.title}
                                        onChange={(e) => updateAdditionalEvent(event.id, 'title', e.target.value)}
                                        leftIcon={<Calendar className="h-4 w-4" />}
                                      />
                                    </div>
                                    
                                    <div className="md:col-span-2">
                                      <CategoryDropdown
                                        options={categories}
                                        value={event.category_id}
                                        onChange={(value) => updateAdditionalEvent(event.id, 'category_id', value)}
                                        placeholder="Select category..."
                                        label="Category"
                                      />
                                    </div>
                                    
                                    <DatePicker
                                      label="Event Date *"
                                      value={event.event_date}
                                      onChange={(value) => updateAdditionalEvent(event.id, 'event_date', value)}
                                      minDate={new Date().toISOString().split('T')[0]}
                                      maxDate={watchedValues.event_date || undefined}
                                      placeholder="Select event date"
                                      helpText={watchedValues.event_date ? "Sub-events can be on the same day as the main event, but not after" : undefined}
                                    />
                                    
                                    <div>
                                      <label className={`block text-sm font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                      }`}>
                                        Location <span className="text-red-500">*</span>
                                      </label>
                                      <div className="h-[42px]">
                                        <Input
                                          variant="mirsal"
                                          placeholder="e.g., Grand Hall, Amman"
                                          value={event.location}
                                          onChange={(e) => updateAdditionalEvent(event.id, 'location', e.target.value)}
                                          leftIcon={<MapPin className="h-4 w-4" />}
                                          className="h-full"
                                          helpText="Venue, City (comma-separated) for WhatsApp invites"
                                        />
                                      </div>
                                    </div>
                                    
                                    <TimePicker
                                      label="Event Time *"
                                      value={event.event_time}
                                      onChange={(value) => updateAdditionalEvent(event.id, 'event_time', value)}
                                      placeholder="Select event time"
                                      minTime={
                                        event.event_date ? (() => {
                                          const selectedDate = new Date(event.event_date);
                                          const today = new Date();
                                          today.setHours(0, 0, 0, 0);
                                          selectedDate.setHours(0, 0, 0, 0);
                                          
                                          // If selected date is today, set minTime to current time
                                          if (selectedDate.getTime() === today.getTime()) {
                                            const now = new Date();
                                            const hours = now.getHours().toString().padStart(2, '0');
                                            const minutes = now.getMinutes().toString().padStart(2, '0');
                                            return `${hours}:${minutes}`;
                                          }
                                          return undefined;
                                        })() : undefined
                                      }
                                    />
                                    
                                    <Input
                                      type="number"
                                      label="Guest Limit"
                                      placeholder="e.g., 50 (leave blank for unlimited)"
                                      variant="mirsal"
                                      value={event.guest_limit || ''}
                                      onChange={(e) => updateAdditionalEvent(event.id, 'guest_limit', e.target.value ? parseInt(e.target.value) : undefined)}
                                      leftIcon={<Users className="h-4 w-4" />}
                                    />
                                    
                                    <Input
                                      label="Location Link"
                                      placeholder="e.g., https://maps.google.com/..."
                                      type="url"
                                      required
                                      variant="mirsal"
                                      value={event.location_link || ''}
                                      onChange={(e) => updateAdditionalEvent(event.id, 'location_link', e.target.value)}
                                      leftIcon={<LinkIcon className="h-4 w-4" />}
                                      helpText="Google Maps (or similar) link for this venue"
                                    />
                                    
                                    <Input
                                      label="Website Link"
                                      placeholder="e.g., https://event-website.com"
                                      type="url"
                                      variant="mirsal"
                                      value={event.website_link || ''}
                                      onChange={(e) => updateAdditionalEvent(event.id, 'website_link', e.target.value)}
                                      leftIcon={<Globe className="h-4 w-4" />}
                                      helpText="Link to event website"
                                    />
                                    
                                    <Input
                                      label="Dress Code"
                                      placeholder="e.g., Formal, Casual, Black Tie"
                                      variant="mirsal"
                                      value={event.dress_code || ''}
                                      onChange={(e) => updateAdditionalEvent(event.id, 'dress_code', e.target.value)}
                                      leftIcon={<Shirt className="h-4 w-4" />}
                                      helpText="Dress code information for the event"
                                    />
                                    
                                    <div className="md:col-span-2">
                                      <label className={`block text-xs font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                      }`}>
                                        Description (Optional)
                                      </label>
                                      <textarea
                                        rows={2}
                                        placeholder="Any specific details for this event"
                                        value={event.description || ''}
                                        onChange={(e) => updateAdditionalEvent(event.id, 'description', e.target.value)}
                                        className={`block w-full p-2 border rounded-lg shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm ${
                                          theme === 'dark' 
                                            ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400' 
                                            : 'border border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                                        }`}
                                      />
                                    </div>
                                    
                                    {/* Usher Service Toggle for Sub-Event */}
                                    <div className="md:col-span-2">
                                      <div className={`border rounded-lg p-4 ${
                                        theme === 'dark' ? 'border-gray-600 bg-gray-800/50' : 'border-gray-300 bg-gray-50'
                                      }`}>
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <h5 className={`text-sm font-medium ${
                                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                              Usher Service
                                            </h5>
                                            <p className={`text-xs mt-1 ${
                                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                              Enable usher service for this event
                                            </p>
                                          </div>
                                          <label className="relative inline-flex items-center cursor-pointer ml-4">
                                            <input
                                              type="checkbox"
                                              checked={event.usher_service_enabled || false}
                                              onChange={async (e) => {
                                                updateAdditionalEvent(event.id, 'usher_service_enabled', e.target.checked);
                                                // Fetch billing data when toggled (for cost preview in sidebar)
                                                setUsherServiceLoading(true);
                                                try {
                                                  const [pricing, balance] = await Promise.all([
                                                    billingService.getCurrentPricing().catch(() => null),
                                                    billingService.getMyBalance().catch(() => null)
                                                  ]);
                                                  setUsherServicePricing(pricing);
                                                  setUsherServiceBalance(balance);
                                                } catch (error) {
                                                  logError('Failed to fetch billing data', error);
                                                } finally {
                                                  setUsherServiceLoading(false);
                                                }
                                              }}
                                              className="sr-only peer"
                                            />
                                            <div className={`w-11 h-6 rounded-full peer ${
                                              event.usher_service_enabled
                                                ? 'bg-mirsal-green' 
                                                : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'
                                            } transition-colors`}>
                                              <div className={`mt-0.5 ml-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                                event.usher_service_enabled ? 'translate-x-5' : ''
                                              }`} />
                                            </div>
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
                </div>

                {/* Action Buttons */}
                <div className={`flex justify-end gap-4 pt-6 mt-6 border-t-2 ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    disabled={loading}
                    className="px-6 py-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={loading}
                    className="px-6 py-2"
                  >
                    {additionalEvents.length > 0 ? 'Create Series' : 'Create Event'}
                  </Button>
                </div>
              </form>
            </div>

          {/* Right Column - Sticky Sidebar (Outside Border) */}
          <div className="lg:col-span-3">
            <div className="sticky top-6 space-y-4">
                {/* SVG Definition for Heart Clip Path - Classic heart shape with proper curves */}
                <svg width="0" height="0" style={{ position: 'absolute' }}>
                  <defs>
                    <clipPath id="heartClipPath" clipPathUnits="objectBoundingBox">
                      {/* Classic heart shape: Start bottom center, two rounded arcs at top, meet at bottom point */}
                      <path d="M0.5,1
                               C0.5,1 0,0.65 0,0.35
                               A0.25,0.25 0 0,1 0.5,0.35
                               A0.25,0.25 0 0,1 1,0.35
                               C1,0.65 0.5,1 0.5,1 Z" />
                    </clipPath>
                  </defs>
                </svg>
                
                {/* Image Upload Section */}
                <div className={`rounded-lg border-2 p-4 ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  {/* Image Shape Selector - Above image */}
                  {eventImage && (
                    <div className="flex gap-2 justify-center mb-3">
                <button
                  type="button"
                  onClick={() => setImageShape('square')}
                  className={`p-2 rounded-md transition-colors shadow-md ${
                    imageShape === 'square'
                      ? 'bg-mirsal-green text-white'
                      : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                  title="Square shape"
                >
                  <Square className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setImageShape('circle')}
                  className={`p-2 rounded-md transition-colors shadow-md ${
                    imageShape === 'circle'
                      ? 'bg-mirsal-green text-white'
                      : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                  title="Circle shape"
                >
                  <Circle className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setImageShape('heart')}
                  className={`p-2 rounded-md transition-colors shadow-md ${
                    imageShape === 'heart'
                      ? 'bg-mirsal-green text-white'
                      : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                  title="Heart shape"
                >
                  <Heart className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setImageShape('star')}
                  className={`p-2 rounded-md transition-colors shadow-md ${
                    imageShape === 'star'
                      ? 'bg-mirsal-green text-white'
                      : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                  title="Star shape"
                >
                  <Star className="h-4 w-4" />
                </button>
                    </div>
                  )}
                  
                  {/* Event Card Preview */}
                  <div className="relative">
              <div 
                className={`aspect-square bg-gray-800 flex items-center justify-center relative overflow-hidden ${getImageShapeStyles(imageShape).className || 'rounded-lg'} ${imageUploading ? '' : 'cursor-pointer'} ${!eventImage ? 'hover:bg-gray-700 transition-colors' : ''}`} 
                style={getImageShapeStyles(imageShape).style}
                onClick={() => {
                  if (!eventImage && !imageUploading) {
                    fileInputRef.current?.click();
                  } else if (eventImage && !imageUploading) {
                    // Allow replacing image by clicking on it
                    fileInputRef.current?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !imageUploading) {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                aria-label={eventImage ? "Click to replace image" : "Click to upload image"}
              >
                {/* Background Image */}
                {eventImage ? (
                  <>
                    <img 
                      src={eventImage} 
                      alt="Event preview" 
                      className={`absolute inset-0 w-full h-full object-contain ${getImageShapeStyles(imageShape).className || ''}`}
                      style={getImageShapeStyles(imageShape).style}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-20" />
                  </>
                ) : (
                  <div className="absolute inset-0 opacity-20">
                    <div className="w-full h-full" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M20 20c0-5.523-4.477-10-10-10s-10 4.477-10 10 4.477 10 10 10 10-4.477 10-10zm-10-8c4.418 0 8 3.582 8 8s-3.582 8-8 8-8-3.582-8-8 3.582-8 8-8z'/%3E%3C/g%3E%3C/svg%3E")`,
                      backgroundSize: '40px 40px'
                    }} />
                  </div>
                )}
                
                {/* Event Preview Content - Only show when image is uploaded */}
                {eventImage && (
                  <div className="relative z-10 text-center p-4 pointer-events-none">
                    <div className="w-12 h-12 bg-mirsal-green rounded-full flex items-center justify-center mx-auto mb-3">
                      <Calendar className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">
                      {watchedValues.title || 'Untitled Event'}
                    </h3>
                  </div>
                )}

                {/* Hover overlay - removed text to prevent "Click to replace image" from showing */}
                {eventImage && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100 pointer-events-none" />
                )}

                {/* Image Upload Controls */}
                <div className="absolute top-2 right-2 z-30" onClick={(e) => e.stopPropagation()}>
                  {eventImage ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const confirmed = await showConfirm('Are you sure you want to delete this image?');
                        if (confirmed) {
                          removeImage();
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors shadow-lg border-2 border-white"
                      title="Remove image"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : (
                    imageUploading && (
                      <div className="bg-mirsal-green/80 text-white p-2 rounded-full shadow-lg">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )
                  )}
                </div>

                {/* Upload Instructions - Only show when no image is uploaded */}
                {!eventImage && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-mirsal-green/20 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Plus className="h-8 w-8 text-mirsal-green" />
                      </div>
                      <p className="text-xs text-gray-400 mb-1">Click to upload image</p>
                      <p className="text-xs text-gray-500">Recommended: 800x800px, max 2MB</p>
                      <p className="text-xs text-gray-500">JPG, PNG, GIF, WebP</p>
                    </div>
                  </div>
                )}

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                    className="hidden"
                  />
                  </div>
                </div>

                {/* Event Summary */}
                <div className={`p-4 rounded-lg border-2 ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <h4 className={`font-semibold mb-3 text-base ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Event Summary</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Category:</span>
                      <span className={`font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {categories.find(c => c.id === watchedValues.category_id)?.name || 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Guests:</span>
                      <span className={`font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {watchedValues.guest_limit || 'Unlimited'}
                      </span>
                    </div>
                    {isSeries && (
                      <div className="flex justify-between items-center">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Events:</span>
                        <span className={`font-medium ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {additionalEvents.length + 1}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Cost Preview */}
                {usherServicePricing && (() => {
                  const { costs, setupFeeInfo } = calculateTotalEventCost();
                  const hasUsherService = watchedValues.usher_service_enabled || additionalEvents.some(e => e.usher_service_enabled);
                  
                  return (
                    <div className={`p-4 rounded-lg border-2 ${
                      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <h4 className={`font-semibold mb-3 text-base ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Total Event Cost
                      </h4>
                      
                      {/* Setup Fee Information */}
                      {usherServicePricing.setup_fee && parseFloat(usherServicePricing.setup_fee) > 0 && (
                        <div className={`mb-3 p-3 rounded-lg ${
                          theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                Event Setup Fee
                              </div>
                              {setupFeeInfo.willCharge ? (
                                <div className={`text-xs mt-1 ${
                                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                  {parseFloat(usherServicePricing.setup_fee).toFixed(2)} JOD
                                </div>
                              ) : (
                                <div className={`text-xs mt-1 flex items-center ${
                                  theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                }`}>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  {setupFeeInfo.reason}
                                </div>
                              )}
                            </div>
                            {setupFeeInfo.willCharge && (
                              <div className={`text-sm font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                {parseFloat(usherServicePricing.setup_fee).toFixed(2)} JOD
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Guest Limit Fee Information */}
                      {watchedValues.guest_limit && watchedValues.guest_limit > 0 && usherServicePricing.guest_base_fee && (
                        <div className={`mb-3 p-3 rounded-lg ${
                          theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                Guest Fee
                              </div>
                              <div className={`text-xs mt-1 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                {watchedValues.guest_limit} guests × {parseFloat(usherServicePricing.guest_base_fee).toFixed(2)} JOD
                              </div>
                            </div>
                            <div className={`text-sm font-semibold ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                              {(watchedValues.guest_limit * parseFloat(usherServicePricing.guest_base_fee)).toFixed(2)} JOD
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Usher Service Section */}
                      {hasUsherService && (
                        <div className={`mb-3 p-3 rounded-lg border ${
                          theme === 'dark' ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className={`text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            Usher Service Fees
                          </div>
                          <div className="space-y-1">
                            {watchedValues.usher_service_enabled && (
                              <div className="flex justify-between text-xs">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                  Main Event
                                </span>
                                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                                  {parseFloat(usherServicePricing.usher_service_fee || '0').toFixed(2)} JOD
                                </span>
                              </div>
                            )}
                            {additionalEvents.map((event, index) => {
                              if (event.usher_service_enabled) {
                                return (
                                  <div key={event.id} className="flex justify-between text-xs">
                                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                      Event {index + 2}
                                    </span>
                                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                                      {parseFloat(usherServicePricing.usher_service_fee || '0').toFixed(2)} JOD
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })}
                            {(() => {
                              const usherCount = (watchedValues.usher_service_enabled ? 1 : 0) + 
                                additionalEvents.filter(e => e.usher_service_enabled).length;
                              if (usherCount > 1) {
                                const totalUsherFee = parseFloat(usherServicePricing.usher_service_fee || '0') * usherCount;
                                return (
                                  <div className={`flex justify-between text-xs font-semibold pt-1 border-t ${
                                    theme === 'dark' ? 'border-gray-700 text-white' : 'border-gray-200 text-gray-900'
                                  }`}>
                                    <span>Total Usher Service</span>
                                    <span>{totalUsherFee.toFixed(2)} JOD</span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Total Cost and Balance */}
                      {costs.length > 0 && (
                        <BillingActionPreview
                          costs={costs}
                          showBalance={true}
                          showBalanceAfter={true}
                          currentBalance={usherServiceBalance}
                          pricing={usherServicePricing}
                          showWarning={true}
                          compact={true}
                          infoText="Costs will be deducted from your balance when the event is created."
                        />
                      )}
                      {costs.length === 0 && (
                        <div className={`text-sm text-center py-2 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          No charges will be applied
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Theme Button */}
      <button
        type="button"
        onClick={() => setShowThemePopup(true)}
        className="fixed right-4 top-1/2 transform -translate-y-1/2 z-50 bg-mirsal-green hover:bg-mirsal-green/90 text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
        title="Change Background Theme"
      >
        <Palette className="h-5 w-5" />
      </button>

      {/* Theme Selection Popup */}
      {showThemePopup && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowThemePopup(false)}
        >
          <div 
            className={`max-w-md w-full max-h-[80vh] overflow-y-auto rounded-lg ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Popup Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-mirsal-green" />
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Background Themes
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowThemePopup(false)}
                className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Info Note */}
            <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 ${
              theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
            }`}>
              <p className={`text-xs ${
                theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
              }`}>
                <strong>Note:</strong> The selected background will be included with the invitation link sent to your guests.
              </p>
            </div>

            {/* Theme Options */}
            <div className="p-4 space-y-3">
              {themesLoading ? (
                <div className="text-center py-8">
                  <div className="h-6 w-6 border-2 border-mirsal-green border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading themes...
                  </p>
                </div>
              ) : Object.keys(backgroundThemes).length > 0 ? (
                <>
                  {/* Static Colors Section */}
                  {Object.entries(backgroundThemes).filter(([_, themeData]: [string, any]) => (themeData as any).type === 'color').length > 0 && (
                    <div className="mb-4">
                      <div className={`text-xs font-semibold mb-2 px-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        COLORS
                      </div>
                      <div className="space-y-2">
                        {Object.entries(backgroundThemes)
                          .filter(([_, themeData]: [string, any]) => (themeData as any).type === 'color')
                          .map(([key, themeData]: [string, any]) => (
                            <div
                              key={key}
                              className={`w-full p-3 rounded-lg border-2 transition-all ${
                                selectedTheme === key
                                  ? 'border-mirsal-green bg-mirsal-green/10'
                                  : theme === 'dark'
                                    ? 'border-gray-600 hover:border-gray-500'
                                    : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => setSelectedTheme(key)}
                                className="flex items-center gap-3 w-full text-left"
                              >
                                <div 
                                  className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 flex-shrink-0"
                                  style={{ backgroundColor: (themeData as any).color_code }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className={`text-sm font-medium ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {(themeData as any).name}
                                  </div>
                                  <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                  }`}>
                                    {(themeData as any).description}
                                  </div>
                                </div>
                                {selectedTheme === key && (
                                  <Check className="h-5 w-5 text-mirsal-green flex-shrink-0" />
                                )}
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {/* GIFs Section */}
                  {Object.entries(backgroundThemes).filter(([_, themeData]: [string, any]) => (themeData as any).type === 'gif' || !(themeData as any).type).length > 0 && (
                    <div>
                      <div className={`text-xs font-semibold mb-2 px-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        ANIMATED BACKGROUNDS
                      </div>
                      <div className="space-y-2">
                        {Object.entries(backgroundThemes)
                          .filter(([_, themeData]: [string, any]) => (themeData as any).type === 'gif' || !(themeData as any).type)
                          .map(([key, themeData]: [string, any]) => (
                  <div
                    key={key}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      selectedTheme === key
                        ? 'border-mirsal-green bg-mirsal-green/10'
                        : theme === 'dark'
                          ? 'border-gray-600 hover:border-gray-500'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                                  onClick={() => setSelectedTheme(key)}
                        className="flex items-center gap-3 flex-1 text-left"
                      >
                        <div 
                                    className="w-12 h-12 rounded-lg border flex-shrink-0 overflow-hidden"
                                    style={{ 
                                      background: (themeData as any).preview,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center'
                                    }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                          <div className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {(themeData as any).name}
                                      </div>
                                      {(themeData as any).is_global && (
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          theme === 'dark' 
                                            ? 'bg-blue-500/20 text-blue-300' 
                                            : 'bg-blue-100 text-blue-700'
                                        }`}>
                                          Global
                                        </span>
                                      )}
                          </div>
                          <div className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {(themeData as any).description}
                          </div>
                        </div>
                        {selectedTheme === key && (
                                    <Check className="h-5 w-5 text-mirsal-green flex-shrink-0" />
                        )}
                      </button>
                      
                                {/* Delete Button - Only show for user's own GIFs */}
                                {key !== 'stars' && (themeData as any).can_delete && (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const confirmed = await showConfirm(`Are you sure you want to delete "${(themeData as any).name}"?`);
                            if (confirmed) {
                                        handleDeleteGif(
                                          key, 
                                          (themeData as any).id, 
                                          (themeData as any).filename
                                        );
                                      }
                                    }}
                                    className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors flex-shrink-0"
                          title="Delete theme"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                   </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    No themes available. Upload a GIF to get started!
                  </p>
                </div>
              )}
            </div>

            {/* GIF Upload Section */}
            <div className={`p-4 border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <Plus className="h-4 w-4 text-mirsal-green" />
                <h4 className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Upload New GIF
                </h4>
              </div>
              
              {/* Admin "For Everyone" Toggle */}
              {authUser?.role === 'admin' && (
                <div className="mb-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Make available to everyone
                      </div>
                      <div className={`text-xs mt-0.5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        All hosts will be able to use this GIF
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isGlobalGif}
                        onChange={(e) => setIsGlobalGif(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${
                        isGlobalGif 
                          ? 'bg-mirsal-green' 
                          : theme === 'dark' 
                            ? 'bg-gray-600' 
                            : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          isGlobalGif ? 'translate-x-5' : 'translate-x-0.5'
                        } mt-0.5`} />
                      </div>
                    </div>
                  </label>
                </div>
              )}
              
              {/* Host "Save for Reuse" Toggle */}
              <div className="mb-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Save for reuse in other events
                    </div>
                    <div className={`text-xs mt-0.5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {saveForReuse 
                        ? 'GIF will be saved to your library for future events' 
                        : 'GIF will only be used for this specific event'}
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={saveForReuse}
                      onChange={(e) => setSaveForReuse(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${
                      saveForReuse 
                        ? 'bg-mirsal-green' 
                        : theme === 'dark' 
                          ? 'bg-gray-600' 
                          : 'bg-gray-300'
                    }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        saveForReuse ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`} />
                    </div>
                  </div>
                </label>
              </div>
              
              <button
                type="button"
                onClick={() => gifInputRef.current?.click()}
                disabled={gifUploading}
                className={`w-full p-4 rounded-lg border-2 border-dashed transition-all ${
                  gifUploading
                    ? 'border-gray-400 bg-gray-100 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'border-gray-600 hover:border-mirsal-green hover:bg-mirsal-green/5'
                      : 'border-gray-300 hover:border-mirsal-green hover:bg-mirsal-green/5'
                }`}
              >
                <div className="text-center">
                  {gifUploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 border-2 border-mirsal-green border-t-transparent rounded-full animate-spin" />
                      <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Uploading...
                      </span>
                    </div>
                  ) : (
                    <>
                      <Plus className="h-8 w-8 mx-auto mb-3 text-mirsal-green" />
                      <p className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Click to upload GIF
                      </p>
                      <p className={`text-xs mt-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Max 5MB, GIF format only
                      </p>
                    </>
                  )}
                </div>
              </button>
              
              {gifUploadError && (
                <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
                  {gifUploadError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for GIF upload */}
      <input
        ref={gifInputRef}
        type="file"
        accept=".gif,image/gif"
        onChange={handleGifFileSelect}
        className="hidden"
      />

      {/* Missing Fields Alert */}
      {showMissingFieldsAlert && (missingFields.length > 0 || missingAdditionalEventFields.length > 0) && (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[130] max-w-lg w-full mx-4 animate-in slide-in-from-top-5 duration-300">
        <div className={`rounded-xl shadow-2xl border-2 ${
          theme === 'dark' 
            ? 'bg-gray-800 border-orange-500/50' 
            : 'bg-white border-orange-500'
        }`}>
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                theme === 'dark' ? 'bg-orange-500/20' : 'bg-orange-100'
              }`}>
                <AlertCircle className={`h-6 w-6 ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-base font-semibold mb-3 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Missing Required Fields
                </h3>
                <p className={`text-sm mb-3 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Please complete the following fields to create your event{additionalEvents.length > 0 ? ' series' : ''}:
                </p>
                <ul className={`space-y-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {/* Main event missing fields */}
                  {missingFields.map((field, index) => (
                    <li key={`main-${index}`} className="flex items-center text-sm">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${
                        theme === 'dark' ? 'bg-orange-400' : 'bg-orange-500'
                      }`}></span>
                      {field}
                    </li>
                  ))}
                  {/* Additional events missing fields */}
                  {missingAdditionalEventFields.map((error, index) => (
                    <li key={`additional-${index}`} className="flex items-start text-sm">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 mt-1.5 ${
                        theme === 'dark' ? 'bg-orange-400' : 'bg-orange-500'
                      }`}></span>
                      <span>
                        <span className="font-medium">Event {error.eventNumber}:</span> Missing {error.fields.join(', ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setShowMissingFieldsAlert(false)}
                className={`flex-shrink-0 rounded-lg p-1.5 transition-colors ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                aria-label="Close alert"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

      {/* Toast Notifications */}
      {formError && (
        <Toast
          message={formError || ''}
          type="error"
          onClose={() => setFormError(null)}
        />
      )}
      {success && (
        <Toast
          message={success || ''}
          type="success"
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Loading Spinner */}
      {loading && <LoadingSpinner />}

      {/* Success Popup Modal */}
      {showSuccessPopup && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-md w-full rounded-lg shadow-xl ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* Modal Header */}
          <div className="flex items-center justify-center p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-mirsal-green rounded-full flex items-center justify-center">
              <Check className="h-8 w-8 text-white" />
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6 text-center">
            <h3 className={`text-xl font-semibold mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {isEventSeries ? 'Event Series Created!' : 'Event Created!'}
            </h3>
            <p className={`text-sm mb-6 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {successMessage}
            </p>
            
            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleViewCreatedEvent}
                variant="primary"
                size="lg"
                className="flex items-center gap-2"
              >
                <Check className="h-4 w-4" />
                {isEventSeries ? 'View First Event' : 'View Event'}
              </Button>
              <Button
                onClick={handleSuccessNavigation}
                variant="outline"
                size="lg"
                className="flex items-center gap-2"
              >
                View All Events
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default CreateEventPage2;
